import type { Preferences } from "../persistence/GameSave";

const STYLE = `
  #options-overlay { position: fixed; inset: 0; background: #1d352966; display: none; z-index: 100; justify-content: center; align-items: center; font: 15px system-ui, sans-serif; color: #314338; }
  #options-overlay.show { display: flex; }
  #options-card { background: #fffdf5; border: 2px solid #b9d8aa; border-radius: 20px; box-shadow: 0 12px 32px #1d352966; padding: 28px 36px; max-width: 480px; width: 90%; max-height: 80vh; overflow-y: auto; }
  #options-card h2 { margin: 0 0 16px; font-size: 20px; color: #31583b; }
  #options-card h3 { margin: 14px 0 6px; font-size: 14px; color: #42694b; text-transform: uppercase; letter-spacing: .5px; }
  #options-card .pref-row { display: flex; align-items: center; justify-content: space-between; padding: 6px 0; }
  #options-card .pref-row label { color: #314338; }
  #options-card .pref-row input[type="range"] { width: 140px; accent-color: #79b889; }
  #options-card .pref-row input[type="checkbox"] { accent-color: #79b889; width: 18px; height: 18px; }
  #options-card .pref-row .val { width: 32px; text-align: right; color: #42694b; font-size: 13px; }
  #options-card table { width: 100%; border-collapse: collapse; }
  #options-card td { padding: 4px 0; vertical-align: top; }
  #options-card td:first-child { width: 44%; color: #42694b; font-weight: 600; }
  #options-card td:last-child { color: #314338; }
  #options-card .close { display: block; margin: 18px auto 0; background: #79b889; color: #173420; border: 2px solid #dff2d8; border-radius: 999px; box-shadow: 0 3px 0 #42694b; padding: 8px 24px; font-size: 15px; font-weight: 600; cursor: pointer; }
  #options-card .close:hover { background: #a7d198; }
`;

const HTML = `
  <div id="options-card">
    <h2>Options</h2>
    <h3>Gamepad</h3>
    <div id="gamepad-status" style="color:#42694b; padding:6px 0;">No controller connected</div>
    <table id="gamepad-controls" style="display:none">
      <tr><td>Left stick / D-pad</td><td>Walk around</td></tr>
      <tr><td>A</td><td>Interact with nearest object</td></tr>
      <tr><td>B</td><td>Close menu / Cancel</td></tr>
      <tr><td>X</td><td>Toggle backpack</td></tr>
      <tr><td>Start (+)</td><td>Toggle this menu</td></tr>
      <tr><td>Capture</td><td>Toggle minimap</td></tr>
    </table>
    <h3>Touch</h3>
    <div class="pref-row">
      <label for="pref-touch-controls">Touch controls</label>
      <input type="checkbox" id="pref-touch-controls" checked>
    </div>
    <h3>Audio</h3>
    <div class="pref-row">
      <label for="pref-music">Music</label>
      <input type="range" id="pref-music" min="0" max="100" value="70">
      <span class="val" id="pref-music-val">70</span>
    </div>
    <div class="pref-row">
      <label for="pref-sound">Sound FX</label>
      <input type="range" id="pref-sound" min="0" max="100" value="80">
      <span class="val" id="pref-sound-val">80</span>
    </div>
    <div class="pref-row">
      <label for="pref-muted">Mute all</label>
      <input type="checkbox" id="pref-muted">
    </div>
    <h3>Advanced</h3>
    <div class="pref-row">
      <label for="pref-mcp">AI agent control (MCP)</label>
      <input type="checkbox" id="pref-mcp" checked>
    </div>
    <button class="close">Close</button>
  </div>
`;

export interface OptionsOverlay {
  toggle(): void;
  close(): void;
  syncPrefs(prefs: Preferences): void;
  setGamepadStatus(connected: boolean, name?: string): void;
  onPreferencesChange(handler: (prefs: Preferences) => void): void;
  readonly element: HTMLElement;
}

export function createOptionsOverlay(): OptionsOverlay {
  const style = document.createElement("style");
  style.textContent = STYLE;
  document.head.appendChild(style);

  const el = document.createElement("div");
  el.id = "options-overlay";
  el.innerHTML = HTML;
  document.body.appendChild(el);

  const gamepadStatus = el.querySelector<HTMLElement>("#gamepad-status")!;
  const gamepadControls = el.querySelector<HTMLElement>("#gamepad-controls")!;
  const prefMusic = el.querySelector<HTMLInputElement>("#pref-music")!;
  const prefMusicVal = el.querySelector<HTMLElement>("#pref-music-val")!;
  const prefSound = el.querySelector<HTMLInputElement>("#pref-sound")!;
  const prefSoundVal = el.querySelector<HTMLElement>("#pref-sound-val")!;
  const prefMuted = el.querySelector<HTMLInputElement>("#pref-muted")!;
  const prefMcp = el.querySelector<HTMLInputElement>("#pref-mcp")!;
  const prefTouchControls = el.querySelector<HTMLInputElement>("#pref-touch-controls")!;

  let preferencesChangeHandler: ((prefs: Preferences) => void) | undefined;
  let minimapVisible = false;

  function emitPrefs(): void {
    preferencesChangeHandler?.({
      musicVolume: Number(prefMusic.value) / 100,
      soundVolume: Number(prefSound.value) / 100,
      muted: prefMuted.checked,
      mcpEnabled: prefMcp.checked,
      minimapVisible,
      touchControlsEnabled: prefTouchControls.checked,
    });
  }

  prefMusic.addEventListener("input", () => {
    prefMusicVal.textContent = prefMusic.value;
    emitPrefs();
  });
  prefSound.addEventListener("input", () => {
    prefSoundVal.textContent = prefSound.value;
    emitPrefs();
  });
  prefMuted.addEventListener("change", emitPrefs);
  prefMcp.addEventListener("change", emitPrefs);
  prefTouchControls.addEventListener("change", emitPrefs);

  el.addEventListener("click", (e) => {
    if (e.target === el) el.classList.remove("show");
  });
  el.querySelector(".close")!.addEventListener("click", () => el.classList.remove("show"));

  return {
    toggle() { el.classList.toggle("show"); },
    close() { el.classList.remove("show"); },
    syncPrefs(prefs) {
      prefMusic.value = String(Math.round(prefs.musicVolume * 100));
      prefMusicVal.textContent = prefMusic.value;
      prefSound.value = String(Math.round(prefs.soundVolume * 100));
      prefSoundVal.textContent = prefSound.value;
      prefMuted.checked = prefs.muted;
      prefMcp.checked = prefs.mcpEnabled;
      minimapVisible = prefs.minimapVisible;
      prefTouchControls.checked = prefs.touchControlsEnabled;
    },
    setGamepadStatus(connected, name) {
      if (connected) {
        gamepadStatus.textContent = `Connected: ${name ?? "controller"}`;
        gamepadControls.style.display = "";
      } else {
        gamepadStatus.textContent = "No controller connected";
        gamepadControls.style.display = "none";
      }
    },
    onPreferencesChange(handler) {
      preferencesChangeHandler = handler;
    },
    get element() { return el; },
  };
}
